import { useCallback, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { isArtifactActionable } from '@/components/artifact/panelMeta';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useJobActivation } from '@/components/artifact/useJobActivation';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { MonitorConfigBody } from '@/spaces/chat/panels/MonitorConfigBody';
import { MonitorPanelFooter } from '@/spaces/chat/panels/MonitorPanelFooter';
import {
	hasRejectedStoredTarget,
	isMonitorShapeComplete,
} from '@/spaces/chat/panels/monitorSummary';
import { trackEvent } from '@/utils/fullstory';

import type { CustomMonitor } from '../../../../../bridge/types/artifact.ts';
import { hasFunnelSibling } from '../../../../../bridge/utils/setupCardItems.ts';

interface CustomMonitorPanelProps {
	artifact: CustomMonitor;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const CustomMonitorPanel = ({
	artifact,
	onBack,
	onClose,
	style,
}: CustomMonitorPanelProps) => {
	const bridgeApi = useBridgeApi();
	useEnsureSetupSnapshot(artifact.process_id);
	const goToNextItem = useGoToNextStep();
	const { handleActivate, activatedView } = useJobActivation({
		artifact,
		title: 'Monitor activated',
		summary:
			"I'll check your sources on schedule and let you know what I find.",
		onClose,
	});

	// One of several setup items vs the sole child. A funnel sibling confirms a
	// setup step (Approve) and advances; a standalone activates directly.
	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	const isFunnelSibling = hasFunnelSibling(snapshot);

	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Monitor confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (artifact.process_id) goToNextItem(artifact.process_id, artifact.id);
		},
	});

	// MonitorConfigBody buffers cadence edits and hands us a flush function; we
	// call it right before activation/approval so the buffered cadence is written
	// exactly once instead of on every keystroke.
	const flushRef = useRef<(() => void) | null>(null);
	const registerFlush = useCallback((flush: () => void) => {
		flushRef.current = flush;
	}, []);

	// A source box holding text the sensing tool cannot use is buffered inside the
	// body and never written to the artifact, so it is invisible in
	// `artifact.content`. Track it here or activation re-enables against the last
	// good stored value while a rejected target is still on screen (ADO-1890).
	const [hasRejectedTarget, setHasRejectedTarget] = useState(() =>
		hasRejectedStoredTarget(artifact.content),
	);

	const handleContentChange = useCallback(
		(updates: Record<string, unknown>) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: updates,
			});
		},
		[bridgeApi, artifact.id],
	);

	const handleActivatePressed = useCallback(() => {
		flushRef.current?.();
		handleActivate();
	}, [handleActivate]);

	const handleApprovePressed = useCallback(() => {
		if (!artifact.process_id) return;
		flushRef.current?.();
		trackEvent('Custom Monitor Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		markSubmitted();
	}, [bridgeApi, artifact.id, artifact.process_id, markSubmitted]);

	const canActivate =
		isArtifactActionable(artifact) &&
		!hasRejectedTarget &&
		isMonitorShapeComplete(artifact.content);

	const overlayView = activatedView ?? submittedView;

	const actionRenderer =
		!overlayView && isArtifactActionable(artifact)
			? () => (
					<MonitorPanelFooter
						activateLabel="Start monitoring"
						isActivateDisabled={!canActivate}
						isFunnelSibling={isFunnelSibling}
						onActivate={handleActivatePressed}
						onApprove={handleApprovePressed}
					/>
				)
			: undefined;

	return (
		// No description prop: the body opens with a line derived from this monitor's
		// own fields, which says the same thing about the specific monitor in front of
		// the reader. A generic sentence above it would be the same claim twice.
		<ChatPanelLayout
			title="Monitor"
			actionRenderer={actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{overlayView ?? (
				<MonitorConfigBody
					artifact={artifact}
					handlers={{
						onContentChange: handleContentChange,
						registerFlush,
						onRejectedTargetChange: setHasRejectedTarget,
					}}
				/>
			)}
		</ChatPanelLayout>
	);
};
